import React, { useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { user_role } from '@/entities/user'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/store/auth'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { toast } from '@/hooks/use-toast'
import { showConfirm } from '@/lib/alerts'
import { useLoader } from '@/contexts/LoaderContext'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/common/page-header'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card } from '@/components/cards/card'
import { AnamnesisList } from '@/components/tools-list/anamnese-list'
import { AnamneseProps, formatted_anamnese_profile_types } from '@/entities/anamnese'
import { CreateAnamneseDialog } from '@/components/dialogs/create-anamnese-dialog'
import { FilePreviewDialog } from '@/components/dialogs/file-preview-dialog'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/use-media-query'

const loc = '@/pages/Anamnesis'

export default function Anamnesis() {
  const { user } = useAuthStore()
  const { showLoader, hideLoader } = useLoader()
  const isMdAndDown = useMediaQuery('(max-width:1123px)')
  const isSmAndDown = useMediaQuery('(max-width:768px)')
  const isXsAndDown = useMediaQuery('(max-width:480px)')
  const [data, setData] = useState([]) 
  const [selectedModel, setSelectedModel] = useState<AnamneseProps>({})
  const [isCreateModelOpen, setIsCreateModelOpen] = useState<boolean>(false)
  const [isViewModelOpen, setIsViewModelOpen] = useState<boolean>(false)
  const tabs: { id: string; label: string }[] = Object.entries(formatted_anamnese_profile_types).map(([key, value]) => ({
    id: key,
    label: value
  }))
  const [currentTab, setCurrentTab] = useState<{ id: string; label: string }>(tabs[0])

  const _handleDelete = async (ref: string) => {
    try {
      const { isConfirmed } = await showConfirm('Deseja mesmo deletar ?')
      if (!isConfirmed) return

      showLoader()

      const response = await api.delete('/', {
        data: {
          action: 'anamnese',
          ref,
          userRef: user?.ref
        }
      })

      if (response.data?.statusCode === HTTP_STATUS_CODE.OK) {
        toast({ variant: 'success', title: 'Sucesso', description: 'Anamnese deletada com sucesso.' })
        await _fetchData(true)
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível deletar a anamnese.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível deletar a anamnese.' })
      console.error(`Unhandled error at ${loc}._handleDelete. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _fetchData = async (refetch: boolean) => {
    try {
      if (!refetch && data.length) return

      showLoader()
      const response = await api.get('/', {
        params: {
          action: 'anamnese',
          userRef: user?.ref
        }
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        setData(response.data.body)
      } else {
        toast({
          variant: 'destructive',
          title: 'Ops ...',
          description: response?.data?.statusMessage || 'Erro ao enviar mensagem!'
        })
      }
    } catch (err) {
      console.error(`Unhandled error at ${loc}._fetchData. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  useEffect(() => {
    _fetchData(false)
  }, [])

  return (
    <>
      <div className="mx-auto">
        <PageHeader title="Modelos de anamnese" description="Estruture suas entrevistas clínicas com base na metodologia Mãe Nutrida" />

        <Card
          title="Selecione o tipo de anamnese"
          description="Escolha o modelo adequado para cada perfil de paciente"
          className="mt-4"
          headerButton={
            (user?.role == user_role.ADMIN || user?.role == user_role.MODERATOR) && (
              <Button
                className={cn('flex items-center rounded-2xl p-2 bg-primary hover:bg-primary/60 dark:text-white text-white text-sm font-medium', isMdAndDown && 'w-full')}
                onClick={() => {
                  setSelectedModel({} as AnamneseProps)
                  setIsCreateModelOpen(true)
                }}
              >
                Adicionar
                <Plus className="h-4 w-4 ml-1" />
              </Button>
            )
          }
        >
          <Tabs className="flex flex-col items-center justify-start min-w-full overflow-y-auto overflow-x-hidden mt-10" value={currentTab.id} defaultValue={tabs[0].id} onValueChange={tabId => setCurrentTab(tabs.find(t => t?.id == tabId))}>
            <TabsList>
              {tabs.map((tab, i) => (
                <TabsTrigger key={i} value={tab.id}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1 w-full pt-4" onKeyDown={evt => evt.key === 'Enter' && evt?.preventDefault()}>
              {tabs.map((tab, i) => (
                <TabsContent key={i} value={tab.id}>
                  <AnamnesisList
                    type={tab.id}
                    data={data}
                    onCreate={(ref: string) => {
                      setSelectedModel(data.find(d => d.ref == ref) as AnamneseProps)
                      setIsCreateModelOpen(true)
                    }}
                    onDelete={_handleDelete}
                    onView={(ref: string) => {
                      setSelectedModel(data.find(d => d.ref == ref) as AnamneseProps)
                      setIsViewModelOpen(true)
                    }}
                  />
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </Card>
      </div>

      {isCreateModelOpen && (
        <CreateAnamneseDialog
          open={isCreateModelOpen}
          anamneseProp={selectedModel}
          cb={() => {
            _fetchData(true)
            setSelectedModel({} as AnamneseProps)
            setIsCreateModelOpen(false)
          }}
        />
      )}

      {isViewModelOpen && <FilePreviewDialog open={isViewModelOpen} url={selectedModel.paths[0] || ''} onClose={() => setIsViewModelOpen(false)} />}
    </>
  )
}
